import { api } from './api'
import type { AuditChange, AuditEntry, AuditHistoryResponse, AuditQueryParams } from '../types/audit'

/** Normalize changes to array; some Lambdas store changes as object { fieldName: { from, to } } */
function normalizeChanges(c: unknown): AuditChange[] {
  if (c == null) return []
  if (Array.isArray(c)) {
    return (c as any[]).map((ch) => ({
      field: ch.field ?? ch.fieldName ?? '',
      previousValue: ch.previousValue ?? ch.before ?? ch.from,
      newValue: ch.newValue ?? ch.after ?? ch.to,
    }))
  }
  if (typeof c === 'object' && !Array.isArray(c)) {
    return Object.entries(c).map(([field, v]) => {
      const val = v as Record<string, unknown>
      return {
        field,
        previousValue: val.previousValue ?? val.before ?? val.from,
        newValue: val.newValue ?? val.after ?? val.to,
      }
    })
  }
  return []
}

function normalizeEntry(entry: any): AuditEntry {
  return {
    ...entry,
    changes: normalizeChanges(entry?.changes),
    metadata: entry?.metadata ?? { tenantId: '', subdomain: '' },
  }
}

export const auditService = {
  /**
   * Get audit history for a specific patient
   */
  getPatientAuditHistory: async (
    patientId: string,
    params?: AuditQueryParams
  ): Promise<AuditHistoryResponse> => {
    const queryParams: Record<string, string> = {}
    if (params?.limit) queryParams.limit = params.limit.toString()
    if (params?.startTime) queryParams.startTime = params.startTime
    if (params?.endTime) queryParams.endTime = params.endTime
    if (params?.changeType) queryParams.changeType = params.changeType

    const response = await api.get<AuditHistoryResponse>(
      `/patients/${patientId}/audit`,
      { params: queryParams }
    )
    const data = response.data
    return {
      ...data,
      entries: (data.entries ?? []).map(normalizeEntry),
    }
  },

  /**
   * Get audit history by user
   */
  getAuditHistoryByUser: async (
    performedBy: string,
    params?: AuditQueryParams
  ): Promise<AuditHistoryResponse> => {
    const queryParams: Record<string, string> = {
      performedBy,
    }
    if (params?.limit) queryParams.limit = params.limit.toString()
    if (params?.startTime) queryParams.startTime = params.startTime
    if (params?.endTime) queryParams.endTime = params.endTime
    if (params?.changeType) queryParams.changeType = params.changeType

    const response = await api.get<AuditHistoryResponse>('/audit', {
      params: queryParams,
    })
    const data = response.data
    return {
      ...data,
      entries: (data.entries ?? []).map(normalizeEntry),
    }
  },

  /**
   * Export audit history as CSV
   */
  exportAuditHistoryAsCsv: (entries: AuditEntry[]): string => {
    const headers = [
      'Timestamp',
      'Change Type',
      'Performed By',
      'Display Name',
      'Reason',
      'Field',
      'Previous Value',
      'New Value',
      'IP Address',
    ]

    const rows = entries.flatMap((entry) => {
      const changes = normalizeChanges(entry.changes)
      if (changes.length === 0) {
        // No field changes, just log the event
        return [
          [
            entry.timestamp,
            entry.changeType,
            entry.performedBy,
            entry.performedByDisplayName || '',
            entry.reason || '',
            'N/A',
            'N/A',
            'N/A',
            entry.metadata.ipAddress || '',
          ],
        ]
      }

      return changes.map((change) => [
        entry.timestamp,
        entry.changeType,
        entry.performedBy,
        entry.performedByDisplayName || '',
        entry.reason || '',
        change.field,
        typeof change.previousValue === 'object'
          ? JSON.stringify(change.previousValue)
          : String(change.previousValue ?? ''),
        typeof change.newValue === 'object'
          ? JSON.stringify(change.newValue)
          : String(change.newValue ?? ''),
        entry.metadata.ipAddress || '',
      ])
    })

    // Escape CSV values
    const escapeCsv = (value: string): string => {
      if (value.includes(',') || value.includes('"') || value.includes('\n')) {
        return `"${value.replace(/"/g, '""')}"`
      }
      return value
    }

    const csvRows = [
      headers.map(escapeCsv).join(','),
      ...rows.map((row) => row.map(escapeCsv).join(',')),
    ]

    return csvRows.join('\n')
  },
}
